import smartDate from "@qrohlf/smart-date";
import cx from "classnames";
import { SimpleMap } from "common/SimpleMap";
import fetchJSON from "lib/fetchJSON";
import { getConsole } from "lib/getConsole";
import "normalize.css/normalize.css";
import PropTypes from "prop-types";
import React from "react";
import styles from "./embed.scss";
import RouteList from "./RouteList";
import TitleBar from "./TitleBar";

const console = getConsole("mre", "MultiRouteEmbed");

export default class MultiRouteEmbed extends React.Component {
  static defaultProps = {
    overlay: "rwgps",
  };

  constructor(props) {
    super(props);
    this.state = {
      event: false,
      selectedRoute: false,
    };
  }

  componentDidMount() {
    this.fetchEventData();
    // WARNING this nukes thickbox for the entire page
    window.tb_show = () => {};
  }

  fetchEventData() {
    return fetchJSON(this.eventUrl("json"))
      .then((data) => {
        this.setState({ event: data.event });
      })
      .catch((e) => {
        console.error(e);
        this.setState({ event: "error" });
      });
  }

  eventUrl(format = null) {
    const { eventId } = this.props;
    return `/events/${eventId}${format ? "." + format : ""}`;
  }

  onRouteSelected = (selectedRoute) => {
    this.setState({ selectedRoute });
  };

  getActiveRoute() {
    const { selectedRoute, event } = this.state;
    const { defaultShowAll } = this.props;
    if (selectedRoute) {
      return selectedRoute;
    } else if (event && event.routes.length > 0) {
      return defaultShowAll ? -1 : event.routes[0].id;
    } else {
      return false;
    }
  }

  getRoutes = () => {
    const { event } = this.state;
    const activeRoute = this.getActiveRoute();
    if (activeRoute === -1) {
      return event.routes.map((r) => r.id);
    } else if (activeRoute) {
      return [activeRoute];
    } else {
      return [];
    }
  };

  render() {
    const { event } = this.state;
    const { title, description, overlay, metricUnits, sampleGraph, hideDownload, theme } = this.props;
    if (!event) {
      return false;
    }
    if (event === "error") {
      return problemLoadingEvent;
    }

    const dark = theme === "dark";

    const eventDates = event.all_day
      ? [event.starts_on, event.ends_on].filter((e) => e)
      : [event.starts_at, event.ends_on && event.ends_at].filter((e) => e);

    const eventDate = eventDates.length > 0 && smartDate(eventDates, { includeTime: !event.all_day });

    if (event.routes.length === 0) {
      return (
        <div className={styles.MultiRouteEmbed}>
          <TitleBar eventUrl={this.eventUrl()} heading={title || event.name} subHeading={description || eventDate} />
          {noPublicRoutes}
        </div>
      );
    }
    return (
      <div className={styles.MultiRouteEmbed}>
        <div
          className={cx(styles.Embed, dark && styles.darkTheme)}
          ref={(el) => {
            this._ref = el;
          }}
        >
          <TitleBar
            eventUrl={this.eventUrl()}
            heading={title || event.name}
            subHeading={description || eventDate}
            hideLogo={dark}
          />
          <div className={styles.MapContainer}>
            <SimpleMap
              experimental
              height="100%"
              defaultOverlay={overlay}
              routeIds={this.getRoutes()}
              sampleGraph={!!sampleGraph}
              mapSettings={{ scrollWheelZoom: false }}
              metricUnits={metricUnits}
            />
          </div>
          <RouteList
            routes={event.routes}
            selectedRoute={this.getActiveRoute()}
            onClick={this.onRouteSelected}
            metricUnits={metricUnits}
            hideDownload={hideDownload}
          />
        </div>
        {dark /* need to pull the logo outside styles.darkTheme so it isn't grayscale */ && (
          <a href={this.eventUrl()} target="_blank">
            <img className={styles.BrandImg} src="/images/logos/horizontal-logo-white.svg" width="100" height="24" />
          </a>
        )}
      </div>
    );
  }
}

MultiRouteEmbed.propTypes = {
  eventId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  title: PropTypes.string,
  overlay: PropTypes.string,
  description: PropTypes.string,
  metricUnits: PropTypes.bool, // the bool types here are throwing out warnings when we populate them with urlbar
  sampleGraph: PropTypes.bool,
  defaultShowAll: PropTypes.bool,
  hideDownload: PropTypes.bool,
  theme: PropTypes.string,
  hot: PropTypes.string,
};

const noPublicRoutes = (
  <p className={styles.errorMessage}>
    There are no public routes associated with this event. If you believe this is an error, please contact the creator
    of the event.
  </p>
);

const problemLoadingEvent = (
  <div className={styles.MultiRouteEmbed}>
    <p className={styles.errorMessage}>
      There was a problem loading the event data associated with this embed. The event may have been deleted, or it may
      be set to private in the event settings. If you believe this is an error, please contact the creator of the event.
    </p>
  </div>
);
